import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';
import {
  Card, CardFooter, CardHeader, Col, Row, Table,
} from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import logger from 'src/util/logger';

import AdminCommunityShowProperty from './AdminCommunityShowProperty';

/**
 * Description
 * @param {object} props
 * @param {object} props.relay
 * @param {object} props.community
 * @returns {React.ReactComponentElement} - AdminCommunityShow component
 */
function AdminCommunityShow({ relay, community = null }) {
  const intl = useIntl();

  const loadMore = () => {
    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { logger.error(error); },
    );
  };

  if (!community) {
    return <Loading />;
  }

  const {
    uuid, externalIdentifier, active, createdAt,
    updatedAt, title, properties,
  } = community;
  const { start: activeStart, finish: activeFinish } = active;

  // Page title
  const pageTitle = intl.formatMessage(
    {
      id: 'admin.admin_community.admin_community_show.page.title',
      defaultMessage: 'Enosi Admin - Community - {title}',
    },
    { title },
  );

  // Breadcrumbs
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.breadcrumbs.admin.label',
    defaultMessage: 'Admin',
  });
  const breadcrumbCommunities = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.breadcrumbs.communities.label',
    defaultMessage: 'Communities',
  });

  // Card headers
  const communityDetailsHeader = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.header',
    defaultMessage: 'Community details',
  });
  const propertiesHeader = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.header',
    defaultMessage: 'Properties',
  });

  // Community details table headers
  const headerActiveStatus = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.active_status.label',
    defaultMessage: 'Active status',
  });
  const headerId = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.id.label',
    defaultMessage: 'ID',
  });
  const headerExtId = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.external_identifier.label',
    defaultMessage: 'Ext ID',
  });
  const headerExtIdCommunityDetailsTableTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.external_identifier.abbr.title',
    defaultMessage: 'External identifier',
  });
  const headerStart = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.start.label',
    defaultMessage: 'Start',
  });
  const headerFinish = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.finish.label',
    defaultMessage: 'Finish',
  });
  const headerTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.title.label',
    defaultMessage: 'Title',
  });
  const headerCreatedAt = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.created_at.label',
    defaultMessage: 'Created at',
  });
  const headerUpdatedAt = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.community_details.table.header.updated_at.label',
    defaultMessage: 'Updated at',
  });

  // Properties table headers
  const headerPropertiesTableTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.table.header.title.label',
    defaultMessage: 'Title',
  });
  const headerPropertiesTableId = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.table.header.id.label',
    defaultMessage: 'ID',
  });
  const headerExtIdPropertiesTableTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.table.external_identifier.abbr.title',
    defaultMessage: 'External identifier',
  });
  const headerPropertiesTableExtId = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.table.header.external_identifier.label',
    defaultMessage: 'Ext ID',
  });
  const headerPropertiesTableActive = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.table.header.active.label',
    defaultMessage: 'Active',
  });

  const headerPropertiesTablePrimaryBillingPoints = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties.table.header.primary_billing_points.label',
    defaultMessage: 'Primary Billing Points',
  });

  // Load more button
  const copyLoadMore = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const copyNoMore = intl.formatMessage({
    id: 'admin.admin_community.admin_community_show.properties_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more properties to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbCommunities, path: '/admin/communities' },
          { name: title },
        ]}
      />

      <h1>
        {title}
      </h1>

      <Row className="clear-both">
        <Col xs={12} sm={6} md={4}>
          <Card>
            <CardHeader>
              {communityDetailsHeader}
            </CardHeader>
            <Table responsive>
              <tbody>
                <tr>
                  <th scope="row" className="text-right">{headerActiveStatus}</th>
                  <td>
                    <BadgeActive start={activeStart} finish={activeFinish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerId}</th>
                  <td>
                    <UUID uuid={uuid} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">
                    <abbr title={headerExtIdCommunityDetailsTableTitle}>{headerExtId}</abbr>
                  </th>
                  <td>{externalIdentifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerStart}</th>
                  <td>
                    {activeStart
                    && DateTime.fromSeconds(activeStart).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerFinish}</th>
                  <td>
                    {activeFinish
                    && DateTime.fromSeconds(activeFinish).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerTitle}</th>
                  <td>{title}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerCreatedAt}</th>
                  <td>
                    {createdAt
                      && DateTime.fromSeconds(createdAt).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerUpdatedAt}</th>
                  <td>
                    {updatedAt
                    && DateTime.fromSeconds(updatedAt).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={8}>
          <Card>
            <CardHeader>
              {propertiesHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col">{headerPropertiesTableTitle}</th>
                  <th scope="col">{headerPropertiesTableId}</th>
                  <th scope="col">
                    <abbr title={headerExtIdPropertiesTableTitle}>
                      {headerPropertiesTableExtId}
                    </abbr>
                  </th>
                  <th scope="col">
                    {headerPropertiesTablePrimaryBillingPoints}
                  </th>
                  <th scope="col">{headerPropertiesTableActive}</th>
                </tr>
              </thead>
              <tbody>
                {properties && properties.edges.map((el) => (
                  <AdminCommunityShowProperty property={el.node} key={el.node?.uuid} />
                ))}
              </tbody>
            </Table>
            <CardFooter>
              <LoadMoreButton
                copyLoadMore={copyLoadMore}
                copyNoMore={copyNoMore}
                onClick={loadMore}
                hasNext={relay.hasMore()}
              />
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </>
  );
}

AdminCommunityShow.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  community: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default createPaginationContainer(
  AdminCommunityShow,
  {
    community: graphql`
      fragment AdminCommunityShow_community on Community {
        uuid
        externalIdentifier
        title
        createdAt
        updatedAt
        active { start finish }
        properties(
          first: $count
          after: $cursor
        ) @connection(key: "AdminCommunityShow_properties") {
          edges {
            node {
              uuid
              title
              externalIdentifier
              active { start finish }
              address {
                line1 line2 city state postcode
              }
              primaryBillingPoints(first: 500) {
                edges {
                  node {
                    uuid
                    identifier
                    active { start finish }
                  }
                }
              }
            }
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.community.properties;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
        uuid: props.community.uuid,
      };
    },
    query: graphql`
      # Pagination query to be fetched upon calling 'loadMore'.
      # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
      query AdminCommunityShow_Pagination_Query(
        $uuid: ID!
        $count: Int!
        $cursor: String
      ) {
        community(uuid: $uuid) {
          ...AdminCommunityShow_community
        }
      }
    `,
  },
);
